import axios, { CancelTokenSource } from 'axios'
import { getNotifications } from 'client/api/notifications'
import { iParamObject } from 'client/Interfaces/notifications/NotificationsInterface'
import { useEffect, useState } from 'react'

/**
 *  Custom hook to handle pagination and infinite scroll in notifications
 */

export default function useGetNotifications(params: iParamObject, pageNumber: number) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(true)
  const [notifications, setNotifications] = useState([])
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState<string | null>(null)

  const apiCall = async (source: CancelTokenSource) => {
    // update params with last evaluated key
    const paramsWithKey = { ...params, lastEvaluatedKey }

    // Check for which filters are active and reset if needed
    if (notifications.length > 0) {
      if (notifications[0].isDeleted !== params.isDeleted) {
        setNotifications([])
        setLastEvaluatedKey(null)
        // Run the query and add results to the notifications array
        try {
          const data = await getNotifications(params, source.token)
          setNotifications((prev) => {
            return [...prev, ...data?.notifications].filter((notification) => Boolean(notification))
          })
          setLastEvaluatedKey(data.lastEvaluatedKey)
          setLoading(false)
        } catch (err) {
          if (axios.isCancel(err)) {
            return
          }
          setError(true)
        }
        return
      }
    }

    // Run the query and add results to the notifications array
    try {
      const data = await getNotifications(paramsWithKey, source.token)
      setNotifications((prev) => {
        return [...prev, ...data?.notifications].filter((notification) => Boolean(notification))
      })
      setLastEvaluatedKey(data.lastEvaluatedKey)
      setLoading(false)
    } catch (err) {
      if (axios.isCancel(err)) {
        return
      }
      setError(true)
    }
  }

  useEffect(() => {
    const cancelToken = axios.CancelToken
    const source = cancelToken.source()

    setLoading(true)
    setError(false)
    apiCall(source)

    return () => source.cancel()
  }, [pageNumber, params.isDeleted])

  return { loading, error, notifications, lastEvaluatedKey }
}
